import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { vi, type Dict, type Lang } from './dictionaries/vi';
import { en } from './dictionaries/en';

const DICTS: Record<Lang, Dict> = { vi, en };
const STORAGE_KEY = 'reda-lang';

function readStored(): string | null {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}
function writeStored(lang: Lang): void {
  try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* private mode: ignore */ }
}

export function detectInitialLang(navigatorLang: string | undefined, saved: string | null): Lang {
  if (saved === 'vi' || saved === 'en') return saved;
  if (navigatorLang && navigatorLang.toLowerCase().startsWith('en')) return 'en';
  return 'vi';
}

function lookup(dict: Dict, key: string): string | undefined {
  let cur: unknown = dict;
  for (const part of key.split('.')) {
    if (cur && typeof cur === 'object' && part in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[part];
    } else return undefined;
  }
  return typeof cur === 'string' ? cur : undefined;
}

function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
}

interface Ctx { lang: Lang; setLang: (l: Lang) => void; }
const LanguageContext = createContext<Ctx | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() =>
    detectInitialLang(typeof navigator !== 'undefined' ? navigator.language : undefined, readStored())
  );

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    writeStored(l);
    setLangState(l);
  }, []);

  const value = useMemo(() => ({ lang, setLang }), [lang, setLang]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang(): Ctx {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used within LanguageProvider');
  return ctx;
}

export function useT() {
  const { lang } = useLang();
  return useCallback(
    (key: string, vars?: Record<string, string | number>): string => {
      const hit = lookup(DICTS[lang], key) ?? lookup(vi, key);
      if (hit === undefined) {
        if (import.meta.env.DEV) console.warn(`[i18n] missing key: ${key}`);
        return key;
      }
      return interpolate(hit, vars);
    },
    [lang]
  );
}

export type { Lang, Dict };
